import React, { useEffect } from 'react';

const QuarterCalculator = ({ matchData, getTeamPlayers, TEAMS, onCalculate }) => {
  useEffect(() => {
    const calculateQuarterInfo = () => {
      if (!matchData) return [];
      
      const teams = TEAMS.filter(team => 
        getTeamPlayers(team.value).length > 0
      );
      
      const teamPlayerCounts = teams.map(team => ({
        team,
        players: getTeamPlayers(team.value),
        count: getTeamPlayers(team.value).length
      }));
      
      const totalPlayers = teamPlayerCounts.reduce((sum, team) => sum + team.count, 0);
      const playTime = matchData.play_time || 120;
      const isPlaberMatch = matchData.is_plaber_match === 1;

      // 플래버 매치인 경우 별도 로직으로 처리
      if (isPlaberMatch) {
        return calculatePlaberMatchQuarters(teams, totalPlayers, playTime, getTeamPlayers);
      }

      let totalQuarters = 0;
      let quarterDurations = [];
      let restPlayersPerTeam = {};
      let keeperCount = 0;
      const maxKeeperTurns = playTime === 60 ? 1 : 2;

      if (playTime === 60) {
        if (totalPlayers === 12) {
          totalQuarters = 6;
          quarterDurations = Array(6).fill(10);
          keeperCount = 1;
        } else if (totalPlayers === 11) {
          totalQuarters = 6;
          quarterDurations = Array(6).fill(10);
          keeperCount = 1;
          teams.forEach(team => {
            const playerCount = getTeamPlayers(team.value).length;
            restPlayersPerTeam[team.value] = playerCount === 6 ? 1 : 0;
          });
        } else if (totalPlayers === 10) {
          totalQuarters = 6;
          quarterDurations = Array(6).fill(10);
          keeperCount = 1;
        }
      } else if (playTime === 120) {
        if (totalPlayers >= 17) {
          totalQuarters = 9;
          quarterDurations = [13, 13, 14, 13, 13, 14, 13, 13, 14];
          keeperCount = 2;
        } else if (totalPlayers >= 15) {
          totalQuarters = 8;
          quarterDurations = Array(8).fill(15);
          keeperCount = 2;
          teams.forEach(team => {
            const playerCount = getTeamPlayers(team.value).length;
            restPlayersPerTeam[team.value] = playerCount === 8 ? 2 : (playerCount === 7 ? 1 : 0);
          });
        } else if (totalPlayers >= 13) {
          totalQuarters = 7;
          quarterDurations = Array(7).fill(17);
          keeperCount = 2;
          teams.forEach(team => {
            const playerCount = getTeamPlayers(team.value).length;
            restPlayersPerTeam[team.value] = playerCount === 7 ? 1 : 0;
          });
        } else if (totalPlayers === 12) {
          totalQuarters = 6;
          quarterDurations = Array(6).fill(20);
          keeperCount = 2;
        } else if (totalPlayers === 11) {
          totalQuarters = 6;
          quarterDurations = Array(6).fill(20);
          keeperCount = 2;
          teams.forEach(team => {
            const playerCount = getTeamPlayers(team.value).length;
            restPlayersPerTeam[team.value] = playerCount === 6 ? 1 : 0;
          });
        } else if (totalPlayers === 10) {
          totalQuarters = 6;
          quarterDurations = Array(6).fill(20);
          keeperCount = 2;
        }
      }

      const matchStartTime = matchData.schedule ? new Date(matchData.schedule) : new Date();
      
      const teamCombinations = [];
      
      if (teams.length === 3) {
        teamCombinations.push([teams[0], teams[1]]);
        teamCombinations.push([teams[1], teams[2]]);
        teamCombinations.push([teams[0], teams[2]]);
      } else if (teams.length === 2) {
        teamCombinations.push([teams[0], teams[1]]);
      }
      
      const teamRotations = {};
      const keeperCounts = {};
      
      teams.forEach(team => {
        const teamName = team.value;
        const allPlayers = getTeamPlayers(teamName);
        
        const nonLatePlayers = allPlayers.filter(p => p.is_late !== 1).sort((a, b) => b.team_number - a.team_number);
        const latePlayers = allPlayers.filter(p => p.is_late === 1).sort((a, b) => b.team_number - a.team_number);
        
        const sortedPlayers = [...nonLatePlayers, ...latePlayers];
        
        teamRotations[teamName] = {
          players: sortedPlayers,
          restIndex: 0,
          keeperIndex: 0
        };
        
        sortedPlayers.forEach(player => {
          const playerId = `${teamName}-${player.team_number}`;
          keeperCounts[playerId] = 0;
        });
      });
      
      const quarters = [];
      
      let currentTime = new Date(matchStartTime);
      
      for (let i = 0; i < totalQuarters; i++) {
        const quarterDuration = quarterDurations[i];
        
        const quarterStartTime = new Date(currentTime);
        
        currentTime = new Date(currentTime);
        currentTime.setMinutes(currentTime.getMinutes() + quarterDuration);
        
        const matchupIndex = i % teamCombinations.length;
        const matchupTeams = teamCombinations[matchupIndex];
        
        const restPlayers = {};
        const keeperPlayers = {};
        const scoreRotationTeams = {};
        
        matchupTeams.forEach(team => {
          const teamName = team.value;
          const rotation = teamRotations[teamName];
          const players = rotation.players;
          const playerCount = players.length;
          
          const restCount = restPlayersPerTeam[teamName] || 0;
          let restPlayerNumbers = [];
          
          if (restCount > 0) {
            for (let j = 0; j < restCount; j++) {
              const restIndex = (rotation.restIndex + j) % playerCount;
              restPlayerNumbers.push(players[restIndex].team_number);
            }
            
            rotation.restIndex = (rotation.restIndex + restCount) % playerCount;
          }
          
          restPlayers[teamName] = restPlayerNumbers;
          
          let keeperPlayerNumbers = [];
          
          let keeperStartIndex = restCount > 0 
            ? Math.floor(playerCount / 2)
            : 0;
          
          let keepersFound = 0;
          let j = 0;
          let allPlayersMaxedOut = true;
          
          while (keepersFound < keeperCount && j < playerCount * 2) {
            const keeperIndex = (keeperStartIndex + rotation.keeperIndex + j) % playerCount;
            const playerNumber = players[keeperIndex].team_number;
            const playerId = `${teamName}-${playerNumber}`;
            
            if (!restPlayerNumbers.includes(playerNumber)) {
              if (keeperCounts[playerId] < maxKeeperTurns) {
                keeperPlayerNumbers.push(playerNumber);
                keeperCounts[playerId]++;
                keepersFound++;
                allPlayersMaxedOut = false;
              }
            }
            
            j++;
          }
          
          rotation.keeperIndex = (rotation.keeperIndex + keeperCount) % playerCount;
          
          if (allPlayersMaxedOut || keeperPlayerNumbers.length < keeperCount) {
            scoreRotationTeams[teamName] = true;
          }
          
          keeperPlayers[teamName] = keeperPlayerNumbers;
        });
        
        quarters.push({
          number: i + 1,
          startTime: quarterStartTime,
          duration: quarterDuration,
          matchup: matchupTeams.map(team => team.value),
          restPlayers,
          keeperPlayers,
          scoreRotationTeams
        });
      }
      
      return quarters;
    };

    // 플래버 매치 쿼터 계산 함수
    const calculatePlaberMatchQuarters = (teams, totalPlayers, playTime, getTeamPlayers) => {
      // 인원 수에 상관없이 6쿼터, 쿼터당 10분으로 통일
      const totalQuarters = 6;
      const quarterDurations = Array(6).fill(10);

      const matchStartTime = matchData.schedule ? new Date(matchData.schedule) : new Date();
      
      const teamCombinations = [];
      
      if (teams.length === 3) {
        teamCombinations.push([teams[0], teams[1]]);
        teamCombinations.push([teams[1], teams[2]]);
        teamCombinations.push([teams[0], teams[2]]);
      } else if (teams.length === 2) {
        teamCombinations.push([teams[0], teams[1]]);
      }
      
      const quarters = [];
      
      let currentTime = new Date(matchStartTime);
      
      for (let i = 0; i < totalQuarters; i++) {
        const quarterDuration = quarterDurations[i];
        
        const quarterStartTime = new Date(currentTime);
        
        currentTime = new Date(currentTime);
        currentTime.setMinutes(currentTime.getMinutes() + quarterDuration);
        
        const matchupIndex = i % teamCombinations.length;
        const matchupTeams = teamCombinations[matchupIndex];
        
        const restPlayers = {};
        const keeperPlayers = {};
        const scoreRotationTeams = {};
        
        matchupTeams.forEach(team => {
          const teamName = team.value;
          const players = getTeamPlayers(teamName)
            .sort((a, b) => b.team_number - a.team_number); // 내림차순 정렬
          
          // 휴식 선수는 표기하지 않음 - 명시적으로 빈 배열로 설정
          restPlayers[teamName] = [];
          
          // 모든 쿼터에서 득점 시 교체로 설정
          scoreRotationTeams[teamName] = true;
          
          // 초기 키퍼는 가장 높은 번호의 선수로 설정 (득점 전까지)
          let keeperPlayerNumbers = [];
          if (players.length > 0) {
            keeperPlayerNumbers.push(players[0].team_number);
          }
          
          keeperPlayers[teamName] = keeperPlayerNumbers;
        });
        
        quarters.push({
          number: i + 1, // 1쿼터부터 시작
          startTime: quarterStartTime,
          duration: quarterDuration,
          matchup: matchupTeams.map(team => team.value),
          restPlayers,
          keeperPlayers,
          scoreRotationTeams,
          // 플래버 매치임을 명시적으로 표시
          isPlaberMatch: true
        });
      }
      
      return quarters;
    };

    const quarters = calculateQuarterInfo();
    onCalculate(quarters);
  }, [matchData, getTeamPlayers, TEAMS, onCalculate]);

  return null; // 이 컴포넌트는 UI를 렌더링하지 않음
};

export default QuarterCalculator; 